import { useState, useEffect } from "react";
import { FileCsv, Flask, Plugs, Globe, Database, MicrosoftExcelLogo } from "@phosphor-icons/react";
import salesforceLogo from "@/assets/salesforce-logo.jpg";

// Curated logos for built-in platforms (override auto-resolution).
const NATIVE = {
  salesforce: salesforceLogo,
  hubspot: "https://cdn.simpleicons.org/hubspot/FF7A59",
  google_sheets: "https://cdn.simpleicons.org/googlesheets/34A853",
  excel: "https://cdn.simpleicons.org/microsoftexcel/217346",
  sap_odata: "https://cdn.simpleicons.org/sap/0FAAFF",
};

function domainFrom(baseUrl) {
  if (!baseUrl || baseUrl.includes("{")) return null; // skip templated hosts
  try {
    const u = new URL(baseUrl.startsWith("http") ? baseUrl : `https://${baseUrl}`);
    return u.hostname;
  } catch {
    return null;
  }
}

// Build an ordered list of candidate logo URLs. The <img> falls through each
// on error, so new connectors get a real logo automatically with zero config.
function buildSources({ platform, logoUrl, baseUrl }) {
  const srcs = [];
  if (logoUrl) srcs.push(logoUrl);
  if (NATIVE[platform]) srcs.push(NATIVE[platform]);
  if (platform && /^[a-z0-9]+$/.test(platform)) srcs.push(`https://cdn.simpleicons.org/${platform}`);
  const domain = domainFrom(baseUrl);
  if (domain) srcs.push(`https://icons.duckduckgo.com/ip3/${domain}.ico`);
  return srcs;
}

export function PlatformLogo({ platform, size = 28, logoUrl, baseUrl }) {
  const [idx, setIdx] = useState(0);
  const srcs = buildSources({ platform, logoUrl, baseUrl });
  useEffect(() => { setIdx(0); }, [platform, logoUrl, baseUrl]);

  const box = size + 12;
  const wrap = "inline-flex items-center justify-center rounded-md bg-white border border-stone-200 shrink-0";
  const src = srcs[idx];
  const cover = platform === "salesforce";

  if (src) {
    return (
      <span className={wrap + " overflow-hidden"} style={{ width: box, height: box }} data-testid={`logo-${platform}`}>
        <img src={src} alt={platform} width={cover ? box : size} height={cover ? box : size}
          style={cover ? { objectFit: "cover" } : { objectFit: "contain" }}
          onError={() => setIdx((i) => i + 1)} />
      </span>
    );
  }

  let Icon = Plugs;
  let color = "#312E81";
  if (platform === "csv") { Icon = FileCsv; color = "#16a34a"; }
  else if (platform === "mock") { Icon = Flask; color = "#7c3aed"; }
  else if (platform === "jsonplaceholder") { Icon = Globe; }
  else if (platform === "database") { Icon = Database; color = "#0ea5e9"; }
  else if (platform === "excel") { Icon = MicrosoftExcelLogo; color = "#217346"; }
  return (
    <span className={wrap} style={{ width: box, height: box }} data-testid={`logo-${platform}`}>
      <Icon size={size} color={color} weight="fill" />
    </span>
  );
}
